import { Box, Text } from '@factorialco/deprecated-design-system'
import groupBy from 'lodash/groupBy'
import sum from 'lodash/sum'

import i18n from 'modules/core/lib/i18n'

import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import type { ShiftsRow } from 'modules/shift_management/lib/types'
import type Shift from 'modules/shift_management/store/internal/models/Shift'

import HoursDisplay, { Element, formatDuration } from './HoursDisplay'

interface Props {
  shiftsRow: ShiftsRow
  isOverMaximum?: boolean
}

const calculateShiftsTotalDuration = (shifts: Shift[]): string => {
  return formatDuration(
    sum(shifts.map((shift) => shift.durationWithBreaks(shift.plannedBreaks.plannedBreaks)))
  )
}

interface ShiftSectionProps {
  title?: string | undefined
  totalDuration: string
  shiftEntries: Array<[string, Shift[]]>
}

const ShiftSection = ({ title, totalDuration, shiftEntries }: ShiftSectionProps) => {
  return (
    <>
      {title && (
        <Box gap="s4" flexDirection="row" justifyContent="spaceBetween" alignItems="center">
          <Text size="200" weight="medium" color="grey1000">
            {title}
          </Text>

          <Box flexDirection="row" alignItems="center" gap="s8">
            <Text size="200" weight="medium" color="grey1000">
              {totalDuration}
            </Text>
          </Box>
        </Box>
      )}
      {shiftEntries.map(([shiftName, shifts]) => (
        <Element
          key={shiftName}
          title={`${shiftName} (${shifts.length})`}
          paddingLeft={title ? 's16' : undefined}
          value={calculateShiftsTotalDuration(shifts)}
        />
      ))}
    </>
  )
}

const PlannedHours = ({ shiftsRow, isOverMaximum = false }: Props) => {
  const [{ view }] = useSchedule()

  const totalDuration = formatDuration(
    shiftsRow.hoursPerRangeAsMilliseconds(view.firstDay, view.lastDay)
  )
  const shiftsByName = groupBy(
    shiftsRow.shiftsPerRange(view.firstDay, view.lastDay),
    (shift) => shift.name || i18n.t('shift_management.hours_information.unnamed_shift')
  )
  const sortedShiftEntries = Object.entries(shiftsByName).sort(([a], [b]) => a.localeCompare(b))

  const extraHoursShiftEntries = sortedShiftEntries.filter(([, shifts]) =>
    shifts.some((shift) => shift.isOvertime)
  )
  const regularShiftEntries = sortedShiftEntries.filter(([, shifts]) =>
    shifts.every((shift) => !shift.isOvertime)
  )

  const regularShifts = regularShiftEntries.flatMap(([, shifts]) => shifts)
  const regularHoursTotalDuration = calculateShiftsTotalDuration(regularShifts)

  const extraHoursShifts = extraHoursShiftEntries.flatMap(([, shifts]) => shifts)
  const extraHoursTotalDuration = calculateShiftsTotalDuration(extraHoursShifts)

  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const isComplementaryHoursEnabled = hasFeatureFlag('dev_time_tracking_complementary_hours')

  return (
    <HoursDisplay
      title={i18n.t('shift_management.hours_information.planned_hours.title')}
      totalDuration={totalDuration}
      hideBody={sortedShiftEntries.length === 0}
      triggerClassName={isOverMaximum ? 'text-f1-foreground-critical' : undefined}
    >
      {!isComplementaryHoursEnabled ? (
        <ShiftSection totalDuration={totalDuration} shiftEntries={sortedShiftEntries} />
      ) : (
        <>
          {regularShiftEntries.length > 0 && (
            <ShiftSection
              title={i18n.t('shift_management.hours_information.planned_hours.regular')}
              totalDuration={regularHoursTotalDuration}
              shiftEntries={regularShiftEntries}
            />
          )}
          {extraHoursShiftEntries.length > 0 && (
            <ShiftSection
              title={i18n.t('shift_management.hours_information.planned_hours.overtime')}
              totalDuration={extraHoursTotalDuration}
              shiftEntries={extraHoursShiftEntries}
            />
          )}
        </>
      )}
    </HoursDisplay>
  )
}

export default PlannedHours
